import { MutationTree, ActionTree } from 'vuex';
import { State, Login, Signup, RecoverPassword, RecoverCustomer } from '@customer/types/customer';
import { toUnderscore, handleLocalStorage } from '@customer/utils/helpers';
import { getCustomerActiveSessionsNumber, getSessionsCookie } from '@customer/utils/getCustomerActiveSessionsNumber';
import getCustomers from '@customer/services/getCustomers';
import { loginApiClient } from '../utils/loginApiClient';

export const state: () => State = () => ({
  isLoggedIn: false,
  token: null,
  profile: null,
  modalVisible: false,
  activeSessions: 0,
  loginFormType: 'check',
});

export const mutations: MutationTree<State> = {
  setLoggedInStatus(state, isLoggedIn: State['isLoggedIn']) {
    state.isLoggedIn = isLoggedIn;
  },
  setProfile(state, profile: State['profile']) {
    state.profile = profile;
  },
  setToken(state, token: State['token']) {
    state.token = token;
  },
  setModalState(state, isVisible: State['modalVisible']) {
    state.modalVisible = isVisible;
  },
  setSessionsNumber(state, sessions: number) {
    state.activeSessions = sessions;
  },
  setLoginFormType(state, loginFormType: State['loginFormType']) {
    state.loginFormType = loginFormType;
  },
};

export const actions: ActionTree<State, Record<string, any>> = {
  async getCustomerData({ commit, dispatch }) {
    const client = await getCustomers();

    // Fallback request if `login_api_init` is fired before listener has been set
    if (client.state === 'customerData') {
      if (client.data) {
        await dispatch('setCustomerData', { data: client.data });
      } else {
        commit('setLoggedInStatus', false);
      }
    } else if (client.state === 'noToken') {
      commit('setLoggedInStatus', false);
    }
  },
  async setCustomerData({ commit }, { data }: { data: Record<string, any> }) {
    const isServer = typeof window === 'undefined';
    const loginData = data?.loginStatus ? data : null;

    if (isServer) {
      return;
    }

    if (!loginData) {
      commit('setLoggedInStatus', false);
      return;
    }

    const client = await loginApiClient();

    const customer = loginData.loginStatus ? loginData : null;
    const clientToken = customer ? await client.getToken() : null;

    commit('setProfile', customer);
    commit('setToken', clientToken);
    commit('setLoggedInStatus', !!customer && !!clientToken);

    handleLocalStorage(clientToken);

    const sessionsCookie = getSessionsCookie();

    if (customer && !sessionsCookie) {
      const sessions = await client.getSessions({ customerId: customer.id });
      const activeSessions = getCustomerActiveSessionsNumber(sessions);
      commit('setSessionsNumber', activeSessions);
    }
  },
  async init() {
    await (await loginApiClient()).init();
  },
  async check(_, { email }: Login['variables']) {
    const client = await loginApiClient();
    const checkResponse = await client.checkEmail({ email });

    return checkResponse.data.available;
  },
  async login(_, { email, password }: Login['variables']) {
    const client = await loginApiClient();
    const loginResponse = await client.login({ email, password });

    if (!loginResponse.data.status) {
      throw new Error('email_or_password_not_valid');
    }
  },
  async logout({ commit }) {
    const client = await loginApiClient();
    await client.logout();
    commit('setLoggedInStatus', false);
    commit('setProfile', null);
    commit('setToken', null);
    commit('setSessionsNumber', 0);
    commit('setLoginFormType', 'check');
  },
  async signup({ dispatch }, { email, password, ...details }: Signup['variables']) {
    // const newsletterTerms = details.newsletterTerms;
    delete details.newsletterTerms;

    const client = await loginApiClient();
    const signupResponse = await client.signup({ email, password, ...details }, true);

    if (signupResponse.errors || !signupResponse.data) {
      throw new Error(toUnderscore(signupResponse.errors[0].error.message));
    }

    await dispatch('setCustomerData');

    return signupResponse.data.status;
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async recoverPassword({ commit }, { email }: RecoverPassword['variables']) {
    const client = await loginApiClient();
    await client.recoverPassword({ email });
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async recoverCustomer({ commit }, { language }: RecoverCustomer['variables']) {
    const client = await loginApiClient();
    await client.recoverCustomer(language);
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async socialSignup({ dispatch }, { type, translations }) {
    const client = await loginApiClient();
    const response = await client.resolveAuthMethod(type, 'signup');
    if (response === undefined) {
      return; // signup was successful
    }
    if (response.signup) {
      const signup = (email?: string) => {
        response
          .signup(email)
          .then(() => {
            alert('account created!');
          })
          .catch((e: any) => {
            if (e.type === 'ResolveAuthNoEmail') {
              const newEmail = prompt('Social media account did not provide an email. Please insert you email address:');
              if (newEmail) {
                signup(newEmail);
              }
            }
            if (e.type === 'EmailNotAvailableError') {
              const newEmail = prompt(translations.email_exists);
              if (newEmail) {
                signup(newEmail);
              }
            }
          });
      };
      const newEmail = prompt(translations.email_exists);
      if (newEmail) {
        signup(newEmail);
      }
    }
    if (response.login) {
      if (confirm(translations.login_instead)) {
        response.login();
      }
    }
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async socialLogin({ dispatch }, { type, translations }) {
    const client = await loginApiClient();
    const response = await client.resolveAuthMethod(type, 'login');
    if (response === undefined) {
      return; // login was successful
    }

    if (response.signup) {
      if (confirm(translations.singup_instead)) {
        const signup = (email?: string) => {
          response
            .signup(email)
            .then(() => {
              alert('account created!');
            })
            .catch((e: any) => {
              if (e.type === 'ResolveAuthNoEmail') {
                const newEmail = prompt(translations.no_email);
                if (newEmail) {
                  signup(newEmail);
                }
              }
              if (e.type === 'EmailNotAvailableError') {
                const newEmail = prompt(translations.email_exists);
                if (newEmail) {
                  signup(newEmail);
                }
              }
            });
        };
        signup();
      }
    }
  },
  async socialLink({ dispatch, commit }, type) {
    let success = false;

    try {
      const response = await (await loginApiClient()).resolveAuthMethod(type, 'link');

      if (response.data.status) {
        await dispatch('getCustomerData');
        success = true;
      }
    } catch (error) {
      console.error('socialLink failed: ', error);

      if (!success) {
        commit(
          'alert',
          {
            type: 'error',
            title: 'portal_login.alert.error_title',
            text: 'portal_login.error.social_link_error',
          },
          { root: true }
        );
      }
    }
  },
  async socialUnlink({ dispatch, commit }, type) {
    let success = false;

    try {
      const response = await (await loginApiClient()).unlinkAuthMethod(type);

      if (response.data.status) {
        await dispatch('getCustomerData');
        success = true;
      }
    } catch (error) {
      console.error('socialUnlink failed: ', error);
    }

    if (!success) {
      commit(
        'alert',
        {
          type: 'error',
          title: 'portal_login.alert.error_title',
          text: 'portal_login.error.social_unlink_error',
        },
        { root: true }
      );
    }
  },
  async createLoginToken(_, { email }: Login['variables']) {
    const client = await loginApiClient();
    const craeateTokenResponse = await client.createLoginToken({ email });

    if (!craeateTokenResponse.data.success) {
      throw new Error('no_account');
    }

    return true;
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async sendDeleteCustomerEmail({ dispatch }, { email, language }) {
    try {
      await (await loginApiClient()).sendDeleteCustomerEmail({ email, language });
    } catch (error) {
      console.error('sendDeleteCustomerEmail failed: ', error);
    }
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async sendVerifyEmail({ dispatch, commit }, language) {
    try {
      await (await loginApiClient()).resendEmailChangeEmail({ language });
      commit(
        'alert',
        {
          type: 'success',
          title: 'portal_login.email_verification_sent',
        },
        { root: true }
      );
    } catch (error) {
      console.error('sendVerifyEmail failed: ', error);
    }
  },
  async updateCustomer({ dispatch }, data) {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve) => {
      const response = await (await loginApiClient()).updateCustomer(data);
      if (response.data && response.data.success) {
        await dispatch('getCustomerData');
        return resolve({ success: true });
      } else {
        return resolve({ errors: response.errors });
      }
    });
  },
  async uploadAvatar({ dispatch }, data) {
    try {
      const response = await (await loginApiClient()).uploadAvatar(data);

      if (response.errors || !response.data) {
        console.error('error during upload before catch', response.errors);
        return;
      }
      await dispatch('getCustomerData');
    } catch (error) {
      console.error('upload catch error', error);
    }
  },
  async removeAvatar({ dispatch }) {
    try {
      const response = await (await loginApiClient()).removeAvatar();

      if (response.errors || !response.data) {
        console.error('error during removeAvatar before catch', response.errors);
        return;
      }
      await dispatch('getCustomerData');
    } catch (error) {
      console.error('removeAvatar catch error', error);
    }
  },
};

export const customerStoreModule = {
  customer: {
    state,
    actions,
    mutations,
    namespaced: true,
  },
};
